import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as ValidatedIcon,
  Pending as PendingIcon,
  ContentCopy as CopyIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import { workloadsApi, installersApi } from '../services/workloadsService';
import { reportUploadError } from '../services/errorReporter';
import { WorkloadDefinition, InstallerMetadata } from '../services/types';
import { TableSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import { formatDateSafe } from '../utils/dateFormat';
import { useApiQuery } from '../hooks/useApiQuery';
import { formatBytes } from '../utils/formatBytes';

/**
 * NC-963: browser single-PUT is unreliable for multi-GB installers (Inventor /
 * hyperMILL deployment ZIPs run to 6.65 GB; single Put Blob ceiling is
 * sv-dependent and memory/SAS-TTL bound). Files above this threshold are routed
 * to the guided-azcopy path instead: the operator runs `azcopy` in a terminal,
 * then confirms — the server verifies integrity against the blob's own
 * Content-MD5 (set by `azcopy --put-md5`), no client SparkMD5 over GBs.
 */
const BROWSER_UPLOAD_MAX_BYTES = 2 * 1024 * 1024 * 1024; // 2 GiB

interface AzcopyUpload {
  uploadUrl: string;
  installerId: string;
  blobName: string;
  expiresInMinutes: number;
}

export default function InstallersPage() {
  const [selectedWorkload, setSelectedWorkload] = useState<string>('');

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [installerToDelete, setInstallerToDelete] = useState<InstallerMetadata | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [markAsLatest, setMarkAsLatest] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  // NC-963: guided-azcopy state for files above the browser ceiling.
  const [azcopy, setAzcopy] = useState<AzcopyUpload | null>(null);
  const [generatingCmd, setGeneratingCmd] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  // AbortController for the current browser upload so "Abbrechen" cancels the
  // in-flight PUT instead of leaving it running in the background.
  const uploadAbortRef = useRef<AbortController | null>(null);

  const { data: workloads, loading: workloadsLoading, error: workloadsError } = useApiQuery<WorkloadDefinition[]>(
    () => workloadsApi.list(false)
  );

  const { data: installers, loading: installersLoading, error: installersError, refetch: refetchInstallers } = useApiQuery<InstallerMetadata[]>(
    () => installersApi.list({ workloadId: selectedWorkload }),
    [selectedWorkload],
    { enabled: !!selectedWorkload }
  );

  const loading = workloadsLoading || installersLoading;
  const error = workloadsError || installersError || uploadError;

  const resetUploadState = () => {
    setSelectedApplication('');
    setSelectedFile(null);
    setVersion('');
    setMarkAsLatest(true);
    setUploadProgress(0);
    setUploadStatus('');
    setUploadError(null);
    setAzcopy(null);
    setGeneratingCmd(false);
    setConfirming(false);
    setCopied(false);
  };

  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true);
    resetUploadState();
  };

  const handleCloseUploadDialog = () => {
    // Cancel any in-flight browser upload before closing.
    uploadAbortRef.current?.abort();
    setUploadDialogOpen(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      // A fresh file may cross the browser/azcopy threshold — drop any
      // previously generated SAS/command and clear stale errors.
      setAzcopy(null);
      setCopied(false);
      setUploadError(null);
    }
  };

  const isLargeFile = !!selectedFile && selectedFile.size > BROWSER_UPLOAD_MAX_BYTES;
  const busy = uploading || generatingCmd || confirming;

  const buildAzcopyCommand = (a: AzcopyUpload): string =>
    `azcopy copy "<lokaler-pfad>/${selectedFile?.name ?? a.blobName}" "${a.uploadUrl}" --put-md5`;

  const handleCopyCommand = async () => {
    if (!azcopy) return;
    try {
      await navigator.clipboard.writeText(buildAzcopyCommand(azcopy));
      setCopied(true);
    } catch {
      // Clipboard may be unavailable (insecure context) — the command is still
      // visible for manual selection, so this is non-fatal.
      setCopied(false);
    }
  };

  // Browser upload path (files ≤ BROWSER_UPLOAD_MAX_BYTES).
  const handleUpload = async () => {
    if (!selectedWorkload || !selectedApplication || !selectedFile || !version) {
      setUploadError('Bitte füllen Sie alle Felder aus');
      return;
    }

    const controller = new AbortController();
    uploadAbortRef.current = controller;

    try {
      setUploading(true);
      setUploadError(null);

      setUploadStatus('Fordere Upload-URL an…');
      const { uploadUrl, installerId } = await installersApi.requestUploadUrl({
        workloadId: selectedWorkload,
        applicationId: selectedApplication,
        fileName: selectedFile.name,
        version,
        sizeBytes: selectedFile.size,
      });

      setUploadStatus('Berechne MD5-Prüfsumme…');
      const md5Hash = await installersApi.computeMD5(selectedFile);

      setUploadStatus('Lade Datei hoch…');
      await installersApi.uploadFile(
        uploadUrl,
        selectedFile,
        md5Hash,
        (progress) => setUploadProgress(progress),
        controller.signal
      );

      setUploadStatus('Bestätige Upload…');
      await installersApi.confirmUpload({
        installerId,
        md5Hash,
        sizeBytes: selectedFile.size,
        markAsLatest,
      });

      setUploadStatus('Upload abgeschlossen!');
      setUploadDialogOpen(false);
      refetchInstallers();
    } catch (err) {
      // NC-1722: the blob PUT bypasses the apiClient interceptor — report it
      // here, or it never shows up under Feedback-und-Fehler.
      reportUploadError(err, { kind: 'installer', fileName: selectedFile.name });
      setUploadError(err instanceof Error ? err.message : 'Fehler beim Upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      uploadAbortRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    uploadAbortRef.current?.abort();
  };

  // Guided-azcopy path step 1: request a write-SAS (size-scaled TTL) and render
  // the copy-paste azcopy command. No file bytes flow through the browser.
  const handleGenerateAzcopy = async () => {
    if (!selectedWorkload || !selectedApplication || !selectedFile || !version) {
      setUploadError('Bitte füllen Sie alle Felder aus');
      return;
    }

    try {
      setGeneratingCmd(true);
      setUploadError(null);
      const { uploadUrl, installerId, blobName, expiresInMinutes } =
        await installersApi.requestUploadUrl({
          workloadId: selectedWorkload,
          applicationId: selectedApplication,
          fileName: selectedFile.name,
          version,
          sizeBytes: selectedFile.size,
        });
      setAzcopy({ uploadUrl, installerId, blobName, expiresInMinutes });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Fehler beim Erzeugen der Upload-URL');
    } finally {
      setGeneratingCmd(false);
    }
  };

  // Guided-azcopy path step 2: confirm without a client MD5. The server verifies
  // against the blob's own Content-MD5 (azcopy --put-md5) — see #1026.
  const handleConfirmAzcopy = async () => {
    if (!azcopy || !selectedFile) return;

    try {
      setConfirming(true);
      setUploadError(null);
      await installersApi.confirmUpload({
        installerId: azcopy.installerId,
        sizeBytes: selectedFile.size,
        markAsLatest,
      });
      setUploadDialogOpen(false);
      refetchInstallers();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Fehler bei der Verifizierung');
    } finally {
      setConfirming(false);
    }
  };

  const handleOpenDeleteDialog = (installer: InstallerMetadata) => {
    setInstallerToDelete(installer);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setInstallerToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!installerToDelete) return;

    try {
      setDeleting(true);
      await installersApi.delete(installerToDelete.id, selectedWorkload);
      handleCloseDeleteDialog();
      refetchInstallers();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Fehler beim Löschen des Installers');
      handleCloseDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  const getCurrentWorkload = (): WorkloadDefinition | undefined => {
    return (workloads || []).find((w) => w.id === selectedWorkload);
  };

  const getApplicationName = (applicationId: string): string => {
    const workload = getCurrentWorkload();
    const app = workload?.applications?.find((a) => a.id === applicationId);
    return app?.displayName || applicationId;
  };

  const getStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'validated':
      case 'active':
        return 'success';
      case 'deprecated':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Ausstehend';
      case 'validated':
        return 'Validiert';
      case 'active':
        return 'Aktiv';
      case 'deprecated':
        return 'Veraltet';
      default:
        return status;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Installer Management</Typography>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={refetchInstallers} size="large">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <ErrorMessage message={error} title="Fehler" onRetry={refetchInstallers} />}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Workload auswählen</InputLabel>
                <Select
                  value={selectedWorkload}
                  onChange={(e) => setSelectedWorkload(e.target.value)}
                  label="Workload auswählen"
                >
                  {(workloads || []).map((workload) => (
                    <MenuItem key={workload.id} value={workload.id}>
                      {workload.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleOpenUploadDialog}
                disabled={!selectedWorkload}
                fullWidth
              >
                Installer hochladen
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <TableSkeleton rows={5} columns={6} />}

      {!loading && selectedWorkload && (installers || []).length === 0 && (
        <EmptyState
          title="Keine Installer vorhanden"
          action={{ label: 'Installer hochladen', onClick: handleOpenUploadDialog }}
        />
      )}

      {!loading && selectedWorkload && (installers || []).length > 0 && (
        <Card>
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Anwendung</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Dateiname</TableCell>
                  <TableCell>Größe</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Hochgeladen</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(installers || []).map((installer) => (
                  <TableRow key={installer.id}>
                    <TableCell>{getApplicationName(installer.applicationId)}</TableCell>
                    <TableCell>
                      {installer.version}
                      {installer.isLatest && (
                        <Chip label="Neueste" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{installer.fileName}</TableCell>
                    <TableCell>{formatBytes(installer.sizeBytes)}</TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          installer.status === 'active' || installer.status === 'validated' ? (
                            <ValidatedIcon />
                          ) : (
                            <PendingIcon />
                          )
                        }
                        label={getStatusLabel(installer.status)}
                        color={getStatusColor(installer.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateSafe(installer.uploadedAt, 'dd.MM.yyyy HH:mm')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Installer löschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(installer)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Installer löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den Installer <strong>{installerToDelete?.fileName}</strong> (Version {installerToDelete?.version}) wirklich löschen?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? 'Wird gelöscht…' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Installer hochladen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Anwendung</InputLabel>
                <Select
                  value={selectedApplication}
                  onChange={(e) => setSelectedApplication(e.target.value)}
                  label="Anwendung"
                  disabled={busy}
                >
                  {getCurrentWorkload()?.applications?.map((app) => (
                    <MenuItem key={app.id} value={app.id}>
                      {app.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                fullWidth
                required
                disabled={busy}
                helperText="z.B. '2024.1.0'"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                disabled={busy}
                startIcon={<UploadIcon />}
              >
                Datei auswählen
                <input type="file" hidden onChange={handleFileSelect} />
              </Button>
              {selectedFile && (
                <Typography variant="caption" display="block" mt={1}>
                  {selectedFile.name} ({formatBytes(selectedFile.size)})
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Browser-Upload bis {formatBytes(BROWSER_UPLOAD_MAX_BYTES)}. Größere Dateien
                (z.B. Inventor-/hyperMILL-ZIPs) werden über den geführten azcopy-Pfad hochgeladen.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={markAsLatest}
                    onChange={(e) => setMarkAsLatest(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Als neueste Version markieren"
              />
            </Grid>

            {uploadError && (
              <Grid item xs={12}>
                <Alert severity="error">{uploadError}</Alert>
              </Grid>
            )}

            {/* Guided-azcopy path (file above the browser ceiling). */}
            {isLargeFile && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Diese Datei ({formatBytes(selectedFile!.size)}) überschreitet das
                  Browser-Upload-Limit von {formatBytes(BROWSER_UPLOAD_MAX_BYTES)}. Bitte laden Sie
                  sie mit <strong>azcopy</strong> hoch und bestätigen Sie anschließend.
                </Alert>

                {!azcopy ? (
                  <Button
                    variant="contained"
                    startIcon={<TerminalIcon />}
                    onClick={handleGenerateAzcopy}
                    disabled={busy || !selectedApplication || !version}
                    fullWidth
                  >
                    {generatingCmd ? 'Erzeuge Befehl…' : 'azcopy-Befehl erzeugen'}
                  </Button>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      1. Führen Sie diesen Befehl im Terminal aus:
                    </Typography>
                    <TextField
                      value={buildAzcopyCommand(azcopy)}
                      fullWidth
                      multiline
                      minRows={2}
                      InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                    />
                    <Button
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={handleCopyCommand}
                      sx={{ mt: 1 }}
                    >
                      {copied ? 'Kopiert!' : 'Befehl kopieren'}
                    </Button>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Der Upload-Link ist {azcopy.expiresInMinutes} Minuten gültig — starten Sie
                      azcopy innerhalb dieses Zeitfensters. Ersetzen Sie &lt;lokaler-pfad&gt; durch
                      den Ordner Ihrer Datei.
                    </Alert>
                    <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                      2. Nach Abschluss von azcopy bestätigen:
                    </Typography>
                  </>
                )}
              </Grid>
            )}

            {/* Browser-upload progress (file at or below the ceiling). */}
            {uploading && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  {uploadStatus}
                </Alert>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" display="block" mt={0.5} align="center">
                  {Math.round(uploadProgress)}%
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          {uploading ? (
            <Button onClick={handleCancelUpload} color="error">
              Abbrechen
            </Button>
          ) : (
            <Button onClick={handleCloseUploadDialog} disabled={busy}>
              {azcopy ? 'Schließen' : 'Abbrechen'}
            </Button>
          )}

          {isLargeFile ? (
            azcopy && (
              <Button
                onClick={handleConfirmAzcopy}
                variant="contained"
                startIcon={<ValidatedIcon />}
                disabled={confirming}
              >
                {confirming ? 'Verifiziere…' : 'Upload bestätigen'}
              </Button>
            )
          ) : (
            <Button onClick={handleUpload} variant="contained" disabled={busy || !selectedFile}>
              {uploading ? 'Hochladen...' : 'Hochladen'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
